<template>
  <div class="sidebar-onboarding">
    <div class="sidebar-onboarding__sub-merchants-button">
      <div class="sidebar-onboarding__sub-merchants-button__logo">
        <img v-if="!fetchingOnboardingData" :src="kycMerchantLogoUrl" alt="" />
      </div>

      <div class="sidebar-onboarding__sub-merchants-button__text">
        <div>
          {{ kycMerchantName }}
        </div>
        <el-tooltip placement="bottom" :content="activeSubMerchantName">
          <div class="ellipsis">
            {{ activeSubMerchantName }}
          </div>
        </el-tooltip>
      </div>
    </div>

    <div class="sidebar-onboarding__menu-wrapper">
      <SidebarItem
        v-for="route in normalizedRoutes"
        :key="route.path"
        :item="route"
        :base-path="route.path"
        @click:arrow="$emit('click:arrow')"
      />
    </div>
  </div>
</template>

<script>
import tamaraSmallLogo from '@/assets/images/tamara-small-logo.png'

// Config the order of KYC routes
const ONBOARDING_PATH = {
  business: '/kyc-business',
  legal: '/kyc-legal',
  bank: '/kyc-bank',
  additional_info: '/kyc-additional-info',
  contact: '/kyc-contact',
}

export default {
  name: 'SidebarOnboarding',
  props: {
    subMerchantAccounts: {
      type: Array,
      default: () => [],
    },
    fetchingOnboardingData: {
      type: Boolean,
      default: false,
    },
    activeSubMerchantName: {
      type: String,
      default: '',
    },
    kycRoutes: {
      type: Array,
      default: () => [],
    },
    onboardingData: {
      type: Object,
      required: true,
    },
  },
  computed: {
    kycMerchantLogoUrl() {
      return this.onboardingData?.businessKyc?.logoUrl &&
        this.onboardingData?.businessKyc?.logo
        ? this.onboardingData?.businessKyc?.logoUrl
        : tamaraSmallLogo
    },

    kycMerchantName() {
      return (
        this.onboardingData?.businessKyc?.businessName ||
        this.$t('sidebar_account')
      )
    },
    onboardingBusinessRoute() {
      const recollectFields =
        this.onboardingData?.kycBusiness?.recollectFields || []

      return {
        isReOpened: false,
        isCompleted: recollectFields.length === 0,
      }
    },
    onboardingLegalRoute() {
      const legalKyc = this.onboardingData.legalKyc

      if (!legalKyc) {
        return {
          isReOpened: false,
          isCompleted: false,
        }
      }

      const recollectFields = legalKyc.recollectFields || []

      if (recollectFields.length > 0) {
        return {
          isReOpened: true,
          isCompleted: true,
        }
      }

      const isAutoCompleted =
        this.isAutomatedLegalOnboarding &&
        this.isAutomatedLegalOnboardingCompleted
      const isManualCompleted =
        !this.isAutomatedLegalOnboarding &&
        this.isManualLegalOnboardingCompleted

      if (isAutoCompleted || isManualCompleted) {
        return {
          isReOpened: false,
          isCompleted: true,
        }
      }

      return {
        isReOpened: false,
        isCompleted: false,
      }
    },
    onboardingBankRoute() {
      const bankKyc = this.onboardingData.bankKyc

      if (!bankKyc) {
        return {
          isReOpened: false,
          isCompleted: false,
        }
      }

      const recollectFields = bankKyc.recollectFields || []

      return {
        isReOpened: recollectFields.length > 0,
        isCompleted: true,
      }
    },
    onboardingAdditionalInfoRoute() {
      return {
        isReOpened: false,
        isCompleted: false,
      }
    },
    onboardingContactRoute() {
      return {
        isReOpened: false,
        isCompleted: false,
      }
    },
    isAutomatedLegalOnboarding() {
      const automatedVerification = this.onboardingData?.automatedVerification

      if (!automatedVerification) {
        return false
      }

      return (
        automatedVerification.crNumber?.isAutomated &&
        automatedVerification.nationalIdNumber?.isAutomated
      )
    },
    isAutomatedLegalOnboardingCompleted() {
      const legalKyc = this.onboardingData?.legalKyc

      if (!legalKyc?.isOtpVerified) {
        return false
      }

      return (
        legalKyc?.recollectFields?.length === 0 && legalKyc?.hasVatId !== null
      )
    },
    isManualLegalOnboardingCompleted() {
      const legalKyc = this.onboardingData?.legalKyc

      if (!legalKyc?.dataSource) {
        return false
      }

      return (
        legalKyc.dataSource.commercialRegistrationSubmittedFrom ===
        'manual_flow'
      )
    },
    normalizedRoutes() {
      return this.kycRoutes.map((route) => {
        const hasChildren = route.children?.length > 0

        if (!hasChildren) {
          return {
            ...route,
          }
        }

        const children = route.children.map((childRoute, index) => ({
          ...childRoute,
          meta: {
            ...childRoute?.meta,
            stepNumber: index + 1,
            ...this.getSidebarItemOnboardingConfig(childRoute),
          },
        }))

        return {
          ...route,
          children,
        }
      })
    },
  },
  methods: {
    getSidebarItemOnboardingConfig(route) {
      const mapping = {
        [ONBOARDING_PATH.business]: this.onboardingBusinessRoute,
        [ONBOARDING_PATH.legal]: this.onboardingLegalRoute,
        [ONBOARDING_PATH.bank]: this.onboardingBankRoute,
        [ONBOARDING_PATH.additional_info]: this.onboardingAdditionalInfoRoute,
        [ONBOARDING_PATH.contact]: this.onboardingContactRoute,
      }

      return mapping[route.path] || {}
    },
  },
}
</script>

<style lang="scss" scoped>
.sidebar-onboarding {
  &__sub-merchants-button {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 14px 24px;
    background-color: #8267db;

    &__logo {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      padding: 6px;
      background-color: #fff;
      border-radius: 6px;
    }

    &__text {
      margin-inline-start: 10px;
      font-size: 14px;
      font-weight: 500;
      line-height: 22px;
      color: #fff;
      text-align: start;
    }
  }

  &__menu-wrapper {
    margin-top: 24px;
  }
}
</style>
